/**
 * Villain & henchman ability hook contracts for the Legendary Arena game engine.
 *
 * Defines the data-only VillainAbilityHook descriptor, the canonical timing
 * and effect-keyword unions with their drift-detection arrays, and a pure
 * query helper. These contracts are built once at setup time (from registry
 * card text) and observed read-only by the Fight/Ambush executor.
 *
 * Mirrors the WP-021 HeroAbilityHook shape. This module imports no game
 * framework and no registry package — contracts only.
 */
// why: drift-detection array — must match the VillainAbilityTiming union
// exactly (the villainAbility.types.test.ts drift test asserts bidirectional
// parity). The three-entry canonical order is locked: 'onAmbush' (city
// entry, WP-185), 'onFight' (defeat, WP-185), 'onEscape' (escape edge,
// WP-186 / D-18601). 'onOverrun' is deliberately absent — `Overrun:` is a
// v1 synonym of `Escape:` and emits `onEscape` at parse time (D-18602).
// Adding a timing requires updating both this array and the union together,
// plus a DECISIONS.md entry.
/**
 * All villain ability timings in canonical order. Single source of truth.
 */
export const VILLAIN_ABILITY_TIMINGS = [
    'onAmbush',
    'onFight',
    'onEscape',
];
// why: drift-detection array — must match the VillainEffectKeyword union
// exactly. The ten-keyword vocabulary is the current locked MVP set. The
// first five (positions 1-5) carry forward unchanged from WP-185
// §Non-Negotiable Constraints — WP-187's executed markers and the overlay
// script's hardcoded copy depend on byte-identical ordering of positions
// 1-5, so insertions are appended at the end only.
// `koHeroEachPlayer` was added at position 6 by WP-189 to close the
// dominant blocked each-player-KO pattern (D-18901): each-player vocabulary
// is expanded incrementally, keyword-by-keyword, only where unconditional
// magnitude-1 patterns are present in the current dataset. Conditional and
// filtered each-player effects (cost-gated, class-gated, "or gains a
// Wound", source-filtered, compound clauses) remain out of scope for the MVP.
// `koHeroEachPlayerMag2` was added at position 7 by WP-202 to close the
// unconditional unfiltered magnitude-2 each-player-KO subset (D-20201):
// magnitude-N each-player-KO uses closed-union-per-magnitude keywords
// appended at position N (`koHeroEachPlayerMag2`, `koHeroEachPlayerMag3`
// if ever needed); parameterized markers (`[effect:koHeroEachPlayer:N]`)
// are rejected for v1 because the parser regex + executor dispatch
// contract + overlay validator + every drift test would all need to
// change for parameterization; the closed-union approach extends the
// WP-189 append-only-position-N pattern with no parser change, and the
// corpus shows only N=2 in v1 (zero N≥3 lines empirically). Any future
// addition requires a new WP plus a DECISIONS.md entry. The order here
// is the canonical emission order for multi-marker lines.
// `captureHqHeroRightmost`, `captureHqHeroHighestCost`,
// `captureHqHeroLowestCost` were added at positions 8, 9, 10 (0-indexed
// 7, 8, 9) by WP-214 to support the v1 HQ hero capture vocabulary
// (D-21401). Append-only; prior positions unchanged.
// why: D-24023 — this union + array are now FROZEN at 10 (the parser's
// legacy-translation input). No keyword is ever appended again; the
// closed-union-per-magnitude (D-20201) + incremental-expansion (D-18901)
// policies are retired in favor of VillainEffectDescriptor below.
/**
 * All villain effect keywords in canonical order. Single source of truth.
 */
export const VILLAIN_EFFECT_KEYWORDS = [
    'gainWoundEachPlayer',
    'gainWoundCurrentPlayer',
    'koHeroCurrentPlayer',
    'heroDeckTopToEscape',
    'captureBystander',
    'koHeroEachPlayer',
    'koHeroEachPlayerMag2',
    'captureHqHeroRightmost',
    'captureHqHeroHighestCost',
    'captureHqHeroLowestCost',
];
// why: drift-detection array — must match VillainEffectPrimitive exactly
// (villainAbility.types.test.ts asserts bidirectional parity). Adding a
// primitive requires updating both this array and the union, plus a DECISIONS
// entry. `scry-ko-own-deck` appended at position 6 by WP-447 (D-24267);
// `gain-attached-hero` appended at position 7 by WP-450 (D-24270);
// `reveal-or-wound` appended at position 8 by WP-469 (D-24281);
// `become-scheme-twist` appended at position 9 by WP-481 (D-24287).
// `draw-cards-current`, `ko-heroes-current-by-trait`, and
// `rescue-bystanders-current-by-trait-count` appended at positions 10, 11, 12 by
// WP-485 (D-24290 — Tier A auto-resolve Core villain effects).
// `gain-wound-unless-victory-villain-group` appended at position 13 by WP-494
// (D-24299 — Viper's conditional each-player wound gated on a Victory-Pile
// villain-group predicate; Tier D).
// `override-next-hand-size` appended at position 14 by WP-503 (D-24307 — the
// core spider-foes Doctor Octopus villain Fight: sets the fighting player's next
// `onBegin` hand-fill target to `magnitude` (8) by writing the WP-497-owned
// shared `G.handSizeOverrides` field; keyword-less auto-resolve, self-narrates).
// `ko-wounds-current-hand-and-discard` appended at position 15 by WP-516 (D-24329 —
// Ymir, Frost Giant King Fight: the current player KOs every Wound from their hand +
// discard; keyword-less no-param auto-resolve, self-narrates).
// `ko-cullable-each-deck-top` appended at position 16 by WP-519 (D-24332 — Melter,
// Masters of Evil Fight: each player reveals their deck top; KO only the cullable ones
// (Wound / basic S.H.I.E.L.D. Agent|Trooper), keep real Heroes + Officer; keyword-less
// no-param auto-resolve, self-narrates; reshuffle-on-empty via shuffleContext).
// `capture-bystanders-plus-per-hq-hero-by-trait` appended at position 17 by WP-521
// (D-24334 — co2e Baron Zemo Ambush: capture 1 Bystander + 1 per HQ Hero matching a
// trait predicate; predicate-parameterized, keyword-less auto-resolve, self-narrates).
// `give-hq-hero-by-trait-to-current` appended at position 18 by WP-522 (D-24335 — co2e
// Ultron Fight: remove the highest-cost HQ Hero matching a trait predicate and give it to
// the current player's discard, refilling the HQ slot; the printed KO-or-gift collapses to
// the gift (dominant in co-op); predicate-parameterized, keyword-less auto-resolve,
// self-narrates).
// why: `swap-two-city-villains` appended at position 19 by WP-523 (D-24336 — co2e
// Whirlwind Ambush: swap the lowest-index and highest-index villain-occupied City spaces
// (Rule B), henchmen excluded, fewer than two is a no-op; the first City board-position
// manipulation; no-param, keyword-less auto-resolve, self-narrates).
// why: `give-hq-hero-each-player` appended at position 20 by WP-532 (D-24343 — Paibok
// the Power Skrull Fight: each player gains one HQ Hero into their discard, HQ refills;
// the second INTERACTIVE villain effect — the current player parks a new
// `pendingGiveHqHeroChoice`, non-current + bot players auto-gain the highest-cost HQ
// Hero; no-param, keyword-less auto-narrating).
// why: `gain-recruit-current` + `gain-officer-current` appended at positions 21 + 22 by
// WP-541 (D-24350 — the first slice of the Core villain/henchman Fight-reward batch: Hand
// Ninjas "+1 recruit" and HYDRA Kidnappers "gain a S.H.I.E.L.D. Officer", both auto-resolve
// rewards to the defeating player; keyword-less, self-narrating).
// why: `add-next-hand-size` appended at position 23 by WP-543 (D-24352 — the ADDITIVE
// next-hand primitive for Savage Land Mutates "draw an extra card"; stacks per defeat, unlike
// the absolute `override-next-hand-size`; keyword-less, self-narrating).
// why: `play-villain-deck-cards` appended at position 24 by WP-542 (D-24351 — the recursive
// villain-deck-play primitive for Endless Armies of HYDRA (Fight, N=2) + The Leader (Ambush,
// N=1); a DELIBERATE NO-OP in the executor (WP-481 secondary-fire-site pattern), the real
// reveal fires from villainDeck.reveal.ts (onAmbush) + fightVillain.ts (onFight); `magnitude`
// carries the play count; keyword-less, the fire sites narrate).
// why: `ko-heroes-current-count-by-trait` appended at position 25 by WP-544 (D-24353 —
// core radiation Maestro Fight: "For each of your [hc:strength] Heroes, KO one of your
// Heroes." The trait supplies only the COUNT; the KO targets are the player's FREE
// interactive choice, so it reuses the `ko-hero` current-player park rather than KOing the
// matching Heroes. Deliberately named `-count-by-trait` (count first) to stay visually
// distinct from `ko-heroes-current-by-trait`, which KOs the MATCHING Heroes;
// predicate-parameterized, keyword-less, self-narrating).
// why: `haunt-hq-hero` appended at position 26 by WP-757 (D-24587 — the Haunt keyword,
// rulebook v23 p.27: The Fallen's Ambush "Haunt the rightmost / leftmost / an unhaunted
// Hero that costs 3 or less". The Villain leaves the City and is recorded as the slot's
// haunter in the lazy `G.hqHaunters`; `selector` picks the slot. Keyword-less,
// self-narrating).
/** All villain effect primitives in canonical order. Single source of truth. */
export const VILLAIN_EFFECT_PRIMITIVES = [
    'ko-hero',
    'gain-wound',
    'capture-hq-hero',
    'hero-deck-top-to-escape',
    'capture-bystander',
    'scry-ko-own-deck',
    'gain-attached-hero',
    'reveal-or-wound',
    'become-scheme-twist',
    'draw-cards-current',
    'ko-heroes-current-by-trait',
    'rescue-bystanders-current-by-trait-count',
    'gain-wound-unless-victory-villain-group',
    'override-next-hand-size',
    'ko-wounds-current-hand-and-discard',
    'ko-cullable-each-deck-top',
    'capture-bystanders-plus-per-hq-hero-by-trait',
    'give-hq-hero-by-trait-to-current',
    'swap-two-city-villains',
    'give-hq-hero-each-player',
    'gain-recruit-current',
    'gain-officer-current',
    'add-next-hand-size',
    'play-villain-deck-cards',
    'ko-heroes-current-count-by-trait',
    'haunt-hq-hero',
    // why: WP-760 / D-24589 — appended at positions 27 and 28. Patriarch's Fight
    // (`reveal-top-draw-if-cost-lte:<N>`: reveal your deck top, draw it if it costs N or
    // less) and Salomé's Fight (`ko-up-to-from-discard-current:<N>`: park a KO-up-to-N
    // choice from your own discard). Both keyword-less, so both self-narrate.
    'reveal-top-draw-if-cost-lte',
    'ko-up-to-from-discard-current',
    // why: WP-816 / D-24749 — appended at position 29. Executioner's Escape ("KO a
    // Bystander from each player's Victory Pile"): every player KOs the first Bystander
    // in their Victory Pile, in pile order, to `G.ko` (the Whispers and Lies precedent).
    // No-param, keyword-less, self-narrates. The marker script's hand-synced copy
    // (`scripts/convert-cards/apply-effect-markers.mjs`) appends it at the same position.
    'ko-bystander-each-victory-pile',
    // why: WP-817 / D-24750 — appended at positions 30 and 31. Captain Zolandia's
    // Fight (`gain-recruit-current-per-hero-class`: +1 Recruit for each Hero Class
    // you have, no params) and Primus's Escape (`discard-hero-each-player-by-cost:
    // <c>[+<c>…]`: each player discards a Hero that costs one of the listed costs,
    // carried on `eligibleCosts`, which stays out of `descriptorKey`). Both
    // keyword-less, so both self-narrate. The marker script's hand-synced copy
    // (`scripts/convert-cards/apply-effect-markers.mjs`) appends them at the same
    // positions.
    'gain-recruit-current-per-hero-class',
    'discard-hero-each-player-by-cost',
    // why: WP-836 / D-24769 — appended at positions 32, 33 and 34. Abomination's Fight
    // (`gain-recruit-current-count-by-trait:<kind>:<value>`: +1 Recruit per Hero you have
    // that matches the trait), Beetle's Fight (`draw-cards-current-count-by-trait:<kind>:
    // <value>`: count the matching Heroes you have, then draw that many) and co2e Savage
    // Land Mutates' Fight (`reveal-top-ko-grey-or-draw-by-class:<c>[+<c>…]`: reveal your
    // deck top; KO it if grey, draw it if it has a listed Hero Class, else leave it). All
    // three keyword-less, so all three self-narrate. The marker script's hand-synced copy
    // (`scripts/convert-cards/apply-effect-markers.mjs`) appends them at the same positions.
    'gain-recruit-current-count-by-trait',
    'draw-cards-current-count-by-trait',
    'reveal-top-ko-grey-or-draw-by-class',
    // why: WP-837 / D-24770 — appended at positions 35, 36, 37 and 38. Mystique's Ambush
    // (`discard-hero-each-player-by-trait:<kind>:<value>`: each player discards a Hero with
    // the trait), Frost Giant Warrior's Ambush (`discard-hero-or-wound-each-player-by-trait:
    // <kind>:<value>`: the same, or gain a Wound instead), and Juggernaut's Ambush as two
    // no-param halves run left to right (`discard-hand-matching-hq-card-names-each-player`,
    // then `hq-heroes-to-hero-deck-bottom`). All four keyword-less, so all four self-narrate.
    // The marker script's hand-synced copy (`scripts/convert-cards/apply-effect-markers.mjs`)
    // appends them at the same positions.
    'discard-hero-each-player-by-trait',
    'discard-hero-or-wound-each-player-by-trait',
    'discard-hand-matching-hq-card-names-each-player',
    'hq-heroes-to-hero-deck-bottom',
];
// why: drift-detection array — must match VillainEffectCondition exactly
// (villainAbility.types.test.ts asserts it at runtime, D-24372). Append-only per
// D-24749: a new condition updates the union, this array, the marker script's
// hand-synced copy (`scripts/convert-cards/apply-effect-markers.mjs`) and the
// executor gate's `switch` arm together, with a DECISIONS entry. An unrecognized
// value at runtime fails closed in the executor.
/** All villain effect conditions in canonical order. Single source of truth. */
export const VILLAIN_EFFECT_CONDITIONS = [
    'savior',
    'another-group-villain-in-city',
];
// why: D-24023 — the frozen legacy-keyword → descriptor translation table. The
// parser maps each legacy `[effect:<keyword>]` marker through this to a
// descriptor, so existing card data keeps working unchanged. Total over the 10
// frozen keywords; each maps to a distinct descriptor (injective — pinned by the
// reverse-map round-trip test).
/** Maps each legacy VillainEffectKeyword to its parameterized descriptor. */
export const LEGACY_VILLAIN_KEYWORD_TO_DESCRIPTOR = {
    gainWoundEachPlayer: { primitive: 'gain-wound', target: 'each' },
    gainWoundCurrentPlayer: { primitive: 'gain-wound', target: 'current' },
    koHeroCurrentPlayer: { primitive: 'ko-hero', target: 'current' },
    heroDeckTopToEscape: { primitive: 'hero-deck-top-to-escape' },
    captureBystander: { primitive: 'capture-bystander' },
    koHeroEachPlayer: { primitive: 'ko-hero', target: 'each', magnitude: 1 },
    koHeroEachPlayerMag2: { primitive: 'ko-hero', target: 'each', magnitude: 2 },
    captureHqHeroRightmost: { primitive: 'capture-hq-hero', selector: 'rightmost' },
    captureHqHeroHighestCost: { primitive: 'capture-hq-hero', selector: 'highest-cost' },
    captureHqHeroLowestCost: { primitive: 'capture-hq-hero', selector: 'lowest-cost' },
};
/**
 * Canonical string key for a descriptor (primitive + its params). Stable field
 * order; absent params render empty. Used to build + query the inverse lookup.
 *
 * @param descriptor - A villain effect descriptor.
 * @returns A stable string key uniquely identifying the descriptor's shape.
 */
function descriptorKey(descriptor) {
    // why: D-24280 — `zone` is DELIBERATELY excluded from the key. A zone-restricted
    // each-player KO (e.g. Juggernaut's `ko-hero:each:2:discard`) shares a key with
    // the zone-less `koHeroEachPlayerMag2` descriptor, so it reverse-maps to that
    // legacy keyword and narrates per-target KO'd-hero names for free (WP-316) —
    // the zone is a resolver-targeting detail invisible to the player-facing label.
    // Adding `zone` here would make the reverse-map return undefined, silencing the
    // KO's narration; do not add it.
    return [
        descriptor.primitive,
        descriptor.target ?? '',
        descriptor.magnitude ?? '',
        descriptor.selector ?? '',
    ].join('|');
}
// why: D-24023 — the inverse lookup (descriptor → its legacy keyword). The
// executor reverse-maps each dispatched descriptor back to a keyword for the
// applied-effects accumulator, so notableEvents / EFFECT_KEYWORD_LABELS / the
// replay state-hash / arena-client stay keyword-typed and byte-identical. Total
// + injective over the 10 legacy descriptors.
/** Inverse of LEGACY_VILLAIN_KEYWORD_TO_DESCRIPTOR: descriptor key → keyword. */
export const DESCRIPTOR_TO_LEGACY_VILLAIN_KEYWORD = new Map(VILLAIN_EFFECT_KEYWORDS.map((keyword) => [
    descriptorKey(LEGACY_VILLAIN_KEYWORD_TO_DESCRIPTOR[keyword]),
    keyword,
]));
/**
 * Returns the legacy keyword for a dispatched descriptor, or undefined for a
 * descriptor that did not originate from a legacy marker (no card uses such a
 * marker in this WP — descriptor-keyed narrative labels are deferred to WP-253).
 *
 * @param descriptor - A dispatched VillainEffectDescriptor.
 * @returns The legacy keyword, or undefined.
 */
export function descriptorToLegacyKeyword(descriptor) {
    return DESCRIPTOR_TO_LEGACY_VILLAIN_KEYWORD.get(descriptorKey(descriptor));
}
// why: drift-detection array — must match VillainDefeatRequirementKind exactly
// (villainAbility.types.test.ts asserts bidirectional parity). Adding a kind
// requires updating both this array and the union together, plus a DECISIONS
// entry. The marker tokens `team` / `hc` map to these two kinds respectively.
/** All villain defeat-requirement kinds in canonical order. Single source of truth. */
export const VILLAIN_DEFEAT_REQUIREMENT_KINDS = [
    'team',
    'hero-class',
];
// ---------------------------------------------------------------------------
// Query helper (pure, read-only)
// ---------------------------------------------------------------------------
/**
 * Returns the hooks for a specific card and timing.
 *
 * Filters by both the card-instance ext_id and the timing label so the
 * executor receives exactly the hooks that should fire at a given fire site.
 *
 * @param hooks - The full villain ability hook table (G.villainAbilityHooks).
 * @param cardId - The card-instance ext_id to match.
 * @param timing - The timing label to match.
 * @returns A freshly-constructed array of matching hooks (never the input).
 */
export function getVillainHooksForCard(hooks, cardId, timing) {
    const result = [];
    for (const hook of hooks) {
        if (hook.cardId === cardId && hook.timing === timing) {
            result.push(hook);
        }
    }
    return result;
}
//# sourceMappingURL=villainAbility.types.js.map